import {
  RyftEditBusinessForm,
  type RyftBusinessFormValues,
  type RyftBusinessSubmissionValues,
} from "../components/RyftEditBusinessForm";
import { Card, CardContent, H2 } from "@sellar-io/components";
import type { BusinessGetResponseType } from "@sellar-io/api-v2/types";
import { useState } from "react";
import { useNavigate } from "react-router";
import { NavigationBar } from "../../../components/navigation/NavigationBar";
import { PageContainer } from "../../../components/pageContainer/PageContainer";
import { BUSINESS_INTEGRATION_RYFT_NON_HOSTED_ONBOARDING } from "../../../constants/routes";
import { apiV2AssertError } from "../../../hooks/api/v2/apiV2Request";
import { useBusiness } from "../../../hooks/api/v2/business/useBusiness";
import { useRyftManagementAccountCreate } from "../../../hooks/api/v2/ryftManagement/useRyftManagementAccountCreate";
import { useSellarCurrentBusiness } from "../../../hooks/useSellarCurrentBusiness.shared";
import { Alert } from "~/components/alert/Alert";
import { EmptyBusinessBoundary } from "./components/EmptyBusinessBoundary";
import { RyftNonHostedOnboardingStepRedirect } from "./components/RyftNonHostedOnboardingStepRedirect";

export function RyftNonHostedOnboardingBusiness() {
  return <RyftNonHostedBusinessLayout />;
}

const RyftNonHostedBusinessLayout = () => {
  return (
    <RyftNonHostedOnboardingStepRedirect stepId="create-business">
      <RyftNonHostedBusinessPageContent />
    </RyftNonHostedOnboardingStepRedirect>
  );
};

const RyftNonHostedBusinessPageContent = () => {
  const navigate = useNavigate();
  const { business } = useSellarCurrentBusiness();
  const businessQuery = useBusiness(business?.id);

  return (
    <>
      <NavigationBar
        title="Set up your business"
        leftIcon="CHEVRON_LEFT"
        onClickLeftIcon={() =>
          navigate(BUSINESS_INTEGRATION_RYFT_NON_HOSTED_ONBOARDING)
        }
      />
      <PageContainer className="mx-auto flex max-w-3xl flex-col gap-6 p-4 md:p-6">
        <EmptyBusinessBoundary
          isLoading={businessQuery.isFetching}
          error={businessQuery.isError ? businessQuery.error : undefined}
          business={businessQuery.data}
        >
          {(loadedBusiness) => (
            <RyftNonHostedBusinessContent
              business={loadedBusiness}
              onComplete={() =>
                navigate(BUSINESS_INTEGRATION_RYFT_NON_HOSTED_ONBOARDING)
              }
            />
          )}
        </EmptyBusinessBoundary>
      </PageContainer>
    </>
  );
};

const RyftNonHostedBusinessContent = ({
  business,
  onComplete,
}: {
  business: BusinessGetResponseType;
  onComplete: () => void;
}) => {
  const createAccountMutation = useRyftManagementAccountCreate();
  const [idempotencyKey] = useState(() => crypto.randomUUID());

  const errorMessage =
    apiV2AssertError(createAccountMutation.error)?.response?.data.message ??
    "Unable to create your Ryft account";

  const handleCreateAccount = async (values: RyftBusinessSubmissionValues) => {
    await createAccountMutation.mutateAsync(
      {
        data: {
          name: values.businessName,
          type: values.businessType,
          registrationNumber: values.companyRegistrationNumber,
          registeredAddress: {
            lineOne: values.addressLine,
            city: values.city,
            country: "GB",
            postalCode: values.postalCode,
          },
          contactEmail: values.contactEmail,
        },
        idempotencyKey,
      },
      {
        onSuccess: onComplete,
      },
    );
  };

  return (
    <>
      <header className="flex flex-col gap-2">
        <H2>Review your business details</H2>
        <p className="max-w-2xl text-sm leading-6 text-zinc-600 md:text-base">
          Ryft uses these details to create and verify your business payout
          account.
        </p>
      </header>

      <Card className="flex flex-col border border-zinc-200 bg-white">
        <CardContent className="flex flex-col gap-4 p-4">
          {createAccountMutation.isError && (
            <Alert type="error" message={errorMessage} className="w-full" />
          )}
          <RyftEditBusinessForm
            mode="create"
            initialData={getInitialFormValues(business)}
            isSubmitting={createAccountMutation.isPending}
            onSubmit={handleCreateAccount}
          />
        </CardContent>
      </Card>
    </>
  );
};

const getInitialFormValues = (
  business: BusinessGetResponseType,
): RyftBusinessFormValues => ({
  businessName: business.registeredCompanyName?.trim() || business.name,
  businessType: "LimitedPartnership",
  companyRegistrationNumber: business.companyNumber ?? "",
  addressLine: [business.InvoiceAddress.name, business.InvoiceAddress.street]
    .filter(Boolean)
    .join(", "),
  city: business.InvoiceAddress.city,
  postalCode: business.InvoiceAddress.postalCode,
  contactEmail: business.email,
});
